import { useMemo, useEffect } from 'react'

import { useLocation, useNavigate } from 'react-router-dom'

import { NetworkVisibility, RoleType } from '@tribeplatform/gql-client/types'

import {
  isUrlPointsWithinApp,
  generateLoginUrl,
} from '../../utils/permission.utils.js'
import { parseSearchParams } from '../../utils/url.utils.js'

export const usePrivateNetworkGuard = (
  roleType: RoleType,
  networkVisibility: NetworkVisibility,
): void => {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const urlPointsWithinApp = useMemo(
    () => isUrlPointsWithinApp(pathname),
    [pathname],
  )
  const isGuest = roleType === RoleType.guest

  useEffect(() => {
    if (!urlPointsWithinApp || !isGuest) {
      return
    }

    const { auth, auto_login: autoLogin } = parseSearchParams(search)

    // Prevent automatic login attempts (SSO, SAML, etc.) if instructed to do so
    const disableAutoLogin = autoLogin === 'false'

    // Redirect guests to the login page if auth=true query param exists
    if (auth === 'true') {
      // navigate method returns void, but consistent-return does not understand it.
      // eslint-disable-next-line consistent-return
      return navigate(
        generateLoginUrl({
          redirect: pathname,
          disableAutoLogin,
        }),
      )
    }

    // Checking for guest users visiting a private network
    if (networkVisibility === NetworkVisibility.private) {
      // navigate method void, but consistent-return does not understand it.
      // eslint-disable-next-line consistent-return
      return navigate(
        generateLoginUrl({
          redirect: pathname,
          disableAutoLogin,
        }),
      )
    }
    // We don't want to run the hook if search changes, hence ignoring it from this list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, pathname, urlPointsWithinApp, isGuest, networkVisibility])
}
